import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Card, Alert } from 'react-bootstrap';
import { FaTimes, FaUpload, FaTrash } from 'react-icons/fa';
import { getApiUrl } from '../../utils/platform';
import { getToken } from '../../utils/token';

const ImageUploadModal = ({ 
  show, 
  onHide, 
  onImagesUploaded, 
  onUploadProgress,
  onUploadStart,
  title = "Upload Ảnh",
  maxImages = 10,
  existingImages = [],
  uploadProgress = 0,
  uploading: externalUploading = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [newImages, setNewImages] = useState([]);
  const fileInputRef = useRef(null);

  // Clear images when modal opens
  useEffect(() => {
    if (show) {
      setNewImages([]);
      setUploading(false);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [show]);

  const handleFileSelect = (files) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    if (file.type.startsWith('image/')) {
      // Upload directly without cropping
      uploadImage(file);
    }
  };

  const uploadImage = async (file) => {
    setUploading(true);
    if (onUploadStart) onUploadStart();
    
    try {
      const formData = new FormData();
      formData.append('images', file);

      const token = getToken();
      const response = await fetch(`${getApiUrl()}/api/upload/images-public`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      
      if (result.status && result.data && result.data.files && result.data.files.length > 0) {
        const uploadedFile = result.data.files[0];
        const newImage = {
          link: uploadedFile.url,
          thumbnail_url: uploadedFile.thumbnail_url || uploadedFile.url, // Use thumbnail if available
          description: ''
        };
        
        setNewImages(prev => [...prev, newImage]);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Lỗi khi upload ảnh. Vui lòng thử lại.');
    } finally {
      setUploading(false);
      if (onUploadProgress) onUploadProgress(0);
    }
  };

  const handleDescriptionChange = (index, description) => {
    setNewImages(prev => 
      prev.map((img, i) => 
        i === index ? { ...img, description } : img
      )
    );
  };

  const handleRemoveImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    onImagesUploaded(newImages);
    setNewImages([]); // Clear the uploaded images
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUpload className="me-2" />
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* File Input */}
        <div className="mb-4">
          <Form.Group>
            <Form.Label>Chọn ảnh để upload:</Form.Label>
            <Form.Control
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileSelect(e.target.files)}
              disabled={uploading}
            />
          </Form.Group>
        </div>

        {/* Upload Status */}
        {(uploading || externalUploading) && (
          <Alert variant="info" className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Đang upload ảnh...
            </div>
            {uploadProgress > 0 && (
              <div className="progress" style={{ height: '8px' }}>
                <div 
                  className="progress-bar progress-bar-striped progress-bar-animated" 
                  role="progressbar" 
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress} 
                  aria-valuemin="0" 
                  aria-valuemax="100"
                >
                  {uploadProgress}%
                </div>
              </div>
            )}
          </Alert>
        )}

        {/* Uploaded Images */}
        {newImages.length > 0 && (
          <div className="mb-4">
            <h6>Ảnh đã upload:</h6>
            <Row>
              {newImages.map((image, index) => (
                <Col key={index} md="4" className="mb-3">
                  <Card>
                    <img 
                      src={image.thumbnail_url || image.link} 
                      alt={`Uploaded ${index + 1}`}
                      className="card-img-top"
                      style={{ height: '150px', objectFit: 'cover' }}
                    />
                    <Card.Body className="p-2">
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Mô tả ảnh..."
                        value={image.description}
                        onChange={(e) => handleDescriptionChange(index, e.target.value)}
                        className="mb-2"
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="w-100"
                        onClick={() => handleRemoveImage(index)}
                      >
                        <FaTrash className="me-1" />
                        Xóa
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          <FaTimes className="me-2" />
          Đóng
        </Button>
        <Button variant="primary" onClick={handleClose}>
          <FaUpload className="me-2" />
          Lưu ảnh
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageUploadModal;